import React, { createContext, useContext, useId } from 'react';

/**
 * Moldura de um campo de formulário — rótulo, marca de obrigatório, dica e erro
 * (at-58, frente C).
 *
 * 🔴 **Existe por causa de um defeito nomeado na spec 03 §4:** a etapa 1 tem 15
 * campos obrigatórios e a única sinalização era o botão desabilitado. *"Não
 * consigo avançar e não sei por quê"* é, nas palavras da spec, **"o pior estado
 * possível no celular"**. Aqui o erro aparece junto do campo, com
 * `role="alert"` — o leitor de tela anuncia sem o usuário ir procurar.
 *
 * ⚠️ **Não valida nada.** Só apresenta o que recebe em `erro`. Quem valida é
 * `utils/validacaoAssociado.ts` (puro) e quem decide *quando* validar é a tela —
 * mesma separação que o `13-ui-kit.md` exige de `Chip` e `EmptyState`.
 *
 * ## A ligação com o controle é por CONTEXTO, não por `cloneElement`
 *
 * `Input`, `Select` e `RadioCards` leem daqui o `id`, o `aria-invalid` e o
 * `aria-describedby` — o consumidor não repassa nada disso à mão. Foi preferido a
 * `cloneElement` (o que o `AppShell` faz com o gatilho do menu) porque ali há um
 * único filho conhecido; aqui o campo do CPF, por exemplo, envolve o input num
 * `<div class="relative">` para pendurar o spinner, e clonar o filho erraria o
 * alvo em silêncio.
 */

interface ContextoCampo {
  /** Id do controle. `htmlFor` do rótulo aponta para ele. */
  id: string;
  /** Id do rótulo — só para grupo (`grupo`), via `aria-labelledby`. */
  idRotulo: string;
  /** Ids da dica e do erro, para o `aria-describedby` do controle. */
  descricao?: string;
  temErro: boolean;
}

const CampoContexto = createContext<ContextoCampo | null>(null);

/**
 * Consumido pelos controles do kit. Fora de um `Field` devolve `null`, e o
 * controle segue funcionando sem as amarras de acessibilidade — é o que permite
 * usar `Input` solto (numa busca, num filtro) sem inventar um rótulo.
 */
export function useCampo(): ContextoCampo | null {
  return useContext(CampoContexto);
}

interface PropsField {
  rotulo: string;
  obrigatorio?: boolean;
  /** Texto de apoio permanente. Some quando há erro, para não competir com ele. */
  dica?: string;
  /** Mensagem de erro. Presente = campo em estado de erro. */
  erro?: string;
  /**
   * Nome do campo para o `data-field`, usado pela tela para **rolar até o
   * primeiro erro** no submit. Sem ele, num formulário de 5 blocos, a mensagem
   * pode aparecer fora da área visível e o efeito volta a ser "não avança e não
   * diz por quê".
   */
  campo?: string;
  /**
   * O conteúdo é um GRUPO de controles (o seletor de sexo), não um só.
   *
   * ⚠️ Muda o rótulo de `<label htmlFor>` para `<span id>`: um `htmlFor`
   * apontando para um grupo não tem alvo válido, e o clique no rótulo focaria a
   * primeira opção como se ela fosse a resposta.
   */
  grupo?: boolean;
  className?: string;
  children: React.ReactNode;
}

const Field: React.FC<PropsField> = ({
  rotulo,
  obrigatorio = false,
  dica,
  erro,
  campo,
  grupo = false,
  className = '',
  children,
}) => {
  const base = useId();
  const id = `${base}-controle`;
  const idRotulo = `${base}-rotulo`;
  const idDica = `${base}-dica`;
  const idErro = `${base}-erro`;

  // A dica sai de cena quando há erro (não é renderizada), então não pode
  // continuar no `aria-describedby` — o leitor anunciaria um id inexistente.
  const descricao = erro ? idErro : dica ? idDica : undefined;

  const marcaObrigatorio = obrigatorio && (
    <span className="tw-ml-0.5 tw-text-brand" aria-hidden="true">
      *
    </span>
  );

  return (
    <div className={['tw-flex tw-flex-col tw-gap-1.5', className].filter(Boolean).join(' ')} data-field={campo}>
      {grupo ? (
        <span id={idRotulo} className="tw-text-sm tw-font-semibold tw-text-texto-titulo">
          {rotulo}
          {marcaObrigatorio}
        </span>
      ) : (
        /*
         * ⚠️ O `id` não é redundante com o `htmlFor` (at-59): quem o consome é o
         * `aria-labelledby` dos campos cujo controle é um BOTÃO que abre modal
         * (`BotaoRegional`, `BotaoSelecao`) — um `<label htmlFor>` não rotula
         * botão. Sem ele aquele `aria-labelledby` apontava para um id inexistente
         * e o rótulo simplesmente **não era anunciado**: o leitor de tela dizia só
         * "Nenhuma cor selecionada", sem dizer de que campo.
         */
        <label id={idRotulo} htmlFor={id} className="tw-text-sm tw-font-semibold tw-text-texto-titulo">
          {rotulo}
          {marcaObrigatorio}
        </label>
      )}

      <CampoContexto.Provider value={{ id, idRotulo, descricao, temErro: !!erro }}>
        {children}
      </CampoContexto.Provider>

      {dica && !erro && <p className="tw-m-0 tw-text-xs tw-text-texto-desabilitado" id={idDica}>{dica}</p>}

      {/*
       * `role="alert"` faz o leitor de tela anunciar assim que a mensagem
       * aparece. ⚠️ O elemento é montado só quando há erro, de propósito: um
       * `role="alert"` sempre presente e vazio não dispara o anúncio em vários
       * leitores (a região precisa MUDAR de conteúdo).
       */}
      {erro && (
        <p role="alert" id={idErro} className="tw-m-0 tw-text-xs tw-font-semibold tw-text-danger">
          {erro}
        </p>
      )}
    </div>
  );
};

export default Field;
